import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  isMissingStripeCheckoutUrl,
  resolveReservationConfirmAction,
} from "../utils/reservationConfirmUtility";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../../../AppContext";
import { useAuthentication } from "../../../../common/infrastructure/hooks/useAuthentication";
import {
  GroupClassWizardStep,
  IGroupClassWizardState,
  IGroupClass,
  IGroupClassInstructor,
  IGroupClassFamilyMember,
} from "../models";
import {
  useGroupClassClasses,
  useGroupClassInstructors,
  useGroupClassFamilyMembersWithStatus,
} from ".";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { routePaths } from "../../../../common/infrastructure/routes/routePaths";
import { getErrorToast } from "../../../../common/infrastructure/utilities/toastUtility";
import { IPreReservationFormSubmission } from "../../../../common/preReservationForms/models";

const STEP_ORDER: GroupClassWizardStep[] = [
  "start",
  "classes",
  "instructors",
  "datetime",
  "login",
  "familyMember",
  "confirmation",
  "success",
];

const isValidStep = (step: string | null): step is GroupClassWizardStep => {
  return step !== null && STEP_ORDER.includes(step as GroupClassWizardStep);
};

interface ICreateGroupClassReservationRequest {
  eventId: number;
  userId: number;
  notes?: string;
  /** When set, the BE pays the visit from this active subscription plan instead of card/link. */
  subscriptionPlanId?: number | null;
  /** When true, pre-populates the reservation's payment method as Multisport. */
  payWithMultisport?: boolean;
  /** The user's completed pre-reservation form, when the event requires one. */
  formSubmission?: IPreReservationFormSubmission | null;
}

export const useGroupClassWizard = () => {
  const { selectedStudio } = useAppContext();
  const authHook = useAuthentication();
  const navigate = useNavigate();
  const isPopstateNavigationRef = useRef(false);
  const initializedFromUrlRef = useRef(false);
  const currentStepRef = useRef<GroupClassWizardStep>("classes");

  // Restoration refs for preventing double-processing
  const restorationProcessedRef = useRef(false);
  const processedBackupRef = useRef<string | null>(null);

  // Track maximum progress to prevent progress bar from going backwards
  const maxProgressRef = useRef(0);
  const prevStepIndexRef = useRef(1);

  // Pending validation state for class and instructor IDs
  const [pendingClassId, setPendingClassId] = useState<string | undefined>();
  const [pendingInstructorId, setPendingInstructorId] = useState<
    number | undefined
  >();

  // Pending flag: user clicked "next" from classes but instructors were still loading
  const [pendingInstructorAutoSkip, setPendingInstructorAutoSkip] =
    useState(false);

  // Auth redirect coordination state
  const [pendingAuthRedirect, setPendingAuthRedirect] = useState(false);

  // Wizard state
  const [wizardState, setWizardState] = useState<IGroupClassWizardState>({
    currentStep: "classes",
    stepIndex: 1,
    totalSteps: STEP_ORDER.length,
    isTransitioning: false,
    canNavigateBack: false,
    canNavigateForward: true,
    selection: {
      class: null,
      instructor: null,
      date: null,
      time: null,
      eventId: null,
    },
    isLoading: false,
    error: null,
    reservationId: null,
    isWaitlistBooking: false,
  });

  // Initialize step from URL on mount and set up browser history
  useEffect(() => {
    if (!initializedFromUrlRef.current) {
      initializedFromUrlRef.current = true;
      const urlParams = new URLSearchParams(window.location.search);
      const stepFromUrl = urlParams.get("step");

      if (isValidStep(stepFromUrl) && stepFromUrl !== "start") {
        const url = new URL(window.location.href);
        url.searchParams.set("step", "start");
        window.history.replaceState(
          { wizardStep: "start" },
          "",
          url.toString(),
        );
      } else {
        const url = new URL(window.location.href);
        url.searchParams.set("step", "start");
        window.history.replaceState(
          { wizardStep: "start" },
          "",
          url.toString(),
        );
      }
    }

    // Handle browser back/forward navigation
    const handlePopState = (event: PopStateEvent) => {
      const stepFromState = event.state?.wizardStep;

      if (currentStepRef.current === "success") {
        isPopstateNavigationRef.current = true;

        setWizardState({
          currentStep: "classes",
          stepIndex: 1,
          totalSteps: STEP_ORDER.length,
          isTransitioning: false,
          canNavigateBack: false,
          canNavigateForward: true,
          selection: {
            class: null,
            instructor: null,
            date: null,
            time: null,
            eventId: null,
          },
          isLoading: false,
          error: null,
          reservationId: null,
          isWaitlistBooking: false,
        });

        const url = new URL(window.location.href);
        url.searchParams.set("step", "start");
        window.history.replaceState(
          { wizardStep: "start" },
          "",
          url.toString(),
        );

        setTimeout(() => {
          isPopstateNavigationRef.current = false;
        }, 0);
        return;
      }

      if (isValidStep(stepFromState)) {
        isPopstateNavigationRef.current = true;

        setWizardState((prev) => {
          const stepIndex = STEP_ORDER.indexOf(stepFromState);
          return {
            ...prev,
            currentStep: stepFromState,
            stepIndex,
            canNavigateBack: stepIndex > 0,
            canNavigateForward: stepIndex < STEP_ORDER.length - 1,
            isTransitioning: false,
          };
        });

        setTimeout(() => {
          isPopstateNavigationRef.current = false;
        }, 0);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Keep currentStepRef in sync with wizard state
  useEffect(() => {
    currentStepRef.current = wizardState.currentStep;
  }, [wizardState.currentStep]);

  // Fetch classes from backend
  const {
    classes: availableClasses,
    categories: classCategories,
    classesByCategory,
    isLoading: classesLoading,
  } = useGroupClassClasses(selectedStudio?.id);

  // Fetch instructors based on selected class
  const {
    instructors: availableInstructors,
    isLoading: instructorsLoading,
  } = useGroupClassInstructors(
    wizardState.selection.class?.className,
    selectedStudio?.id,
  );

  // Fetch family members with reservation status for the selected event (only when authenticated and event selected)
  const {
    familyMembers: availableFamilyMembers,
    isLoading: familyMembersLoading,
    hasFamilyMembers,
  } = useGroupClassFamilyMembersWithStatus(
    selectedStudio?.id,
    wizardState.selection.eventId ?? undefined,
    authHook.isAuthenticated && !!wizardState.selection.eventId, // Only fetch when authenticated and event selected
  );

  // Reservation creation mutation
  const reservationMutation = useApiMutation<
    { reservationId: number; success: boolean; message: string; isWaitlist: boolean; stripeCheckoutUrl?: string }
  >();

  // Helper to update URL with current step
  const updateUrlWithStep = useCallback(
    (step: GroupClassWizardStep, shouldPushState: boolean) => {
      if (step === "success") return;

      const url = new URL(window.location.href);
      url.searchParams.set("step", step);

      if (shouldPushState) {
        window.history.pushState({ wizardStep: step }, "", url.toString());
      } else {
        window.history.replaceState({ wizardStep: step }, "", url.toString());
      }
    },
    [],
  );

  // Step navigation
  const goToStep = useCallback(
    (step: GroupClassWizardStep, options?: { skipHistoryPush?: boolean }) => {
      const { isAuthenticated, currentUser } = authHook;
      const stepIndex = STEP_ORDER.indexOf(step);
      if (stepIndex === -1) return;

      // Check auth requirement at confirmation step
      if (step === "confirmation" && !isAuthenticated && !currentUser?.id) {
        step = "login";
      }

      const finalStepIndex = STEP_ORDER.indexOf(step);

      setWizardState((prev) => ({
        ...prev,
        currentStep: step,
        stepIndex: finalStepIndex,
        canNavigateBack: finalStepIndex > 0,
        canNavigateForward: finalStepIndex < STEP_ORDER.length - 1,
        isTransitioning: false,
      }));

      if (!options?.skipHistoryPush && !isPopstateNavigationRef.current) {
        updateUrlWithStep(step, true);
      }
    },
    [authHook, updateUrlWithStep],
  );

  const nextStep = useCallback(() => {
    const { isAuthenticated, currentUser } = authHook;
    const currentStep = wizardState.currentStep;
    let nextIndex = wizardState.stepIndex + 1;

    // Skip instructor selection if there's only one instructor (excluding "Без значение")
    const realInstructors = availableInstructors.filter(
      (inst) => inst.id !== -1,
    );
    if (currentStep === "classes") {
      if (instructorsLoading) {
        // Instructors not yet loaded — wait for them before deciding whether to skip
        setPendingInstructorAutoSkip(true);
        return;
      }
      if (realInstructors.length === 1) {
        // Auto-select the single instructor
        const singleInstructor = realInstructors[0];
        setWizardState((prev) => ({
          ...prev,
          selection: {
            ...prev.selection,
            instructor: singleInstructor,
            // Reset dependent selections
            date: null,
            time: null,
            eventId: null,
          },
        }));

        // Skip to datetime step
        nextIndex = wizardState.stepIndex + 2;
      }
    }

    // Skip login step if user is already authenticated
    if (nextIndex < STEP_ORDER.length && STEP_ORDER[nextIndex] === "login") {
      if (isAuthenticated && currentUser) {
        nextIndex = nextIndex + 1;
      }
    }

    // Note: We don't skip family member step here anymore
    // The step will handle auto-proceeding based on loaded data

    if (nextIndex < STEP_ORDER.length) {
      goToStep(STEP_ORDER[nextIndex]);
    }
  }, [
    wizardState.stepIndex,
    wizardState.currentStep,
    availableInstructors,
    availableFamilyMembers,
    hasFamilyMembers,
    instructorsLoading,
    goToStep,
    authHook,
  ]);

  const previousStep = useCallback(() => {
    const { isAuthenticated } = authHook;
    const currentStep = wizardState.currentStep;
    let prevIndex = wizardState.stepIndex - 1;

    // Skip instructor selection step when going back if there's only one instructor
    const realInstructors = availableInstructors.filter(
      (inst) => inst.id !== -1,
    );
    if (currentStep === "datetime" && realInstructors.length === 1) {
      prevIndex = wizardState.stepIndex - 2;
    }

    // Skip steps that should be auto-skipped when going back
    // Loop to handle multiple consecutive skips (e.g., both login and familyMember)
    while (prevIndex >= 0 && STEP_ORDER[prevIndex] !== "start") {
      const stepToCheck = STEP_ORDER[prevIndex];

      // Skip login step if user is authenticated
      if (stepToCheck === "login" && isAuthenticated) {
        prevIndex = prevIndex - 1;
        continue;
      }

      // Skip family member step if user has no family members
      if (stepToCheck === "familyMember" && !hasFamilyMembers) {
        prevIndex = prevIndex - 1;
        continue;
      }

      // No skip needed, break out of loop
      break;
    }

    // If we would go back to "start" step or earlier, navigate to appointment wizard homepage instead
    if (prevIndex < 0 || STEP_ORDER[prevIndex] === "start") {
      navigate(routePaths.portalAppointment);
    } else {
      goToStep(STEP_ORDER[prevIndex]);
    }
  }, [
    wizardState.stepIndex,
    wizardState.currentStep,
    availableInstructors,
    hasFamilyMembers,
    goToStep,
    authHook,
    navigate,
  ]);

  // Selection methods
  const selectClass = useCallback((groupClass: IGroupClass) => {
    setWizardState((prev) => ({
      ...prev,
      selection: {
        ...prev.selection,
        class: groupClass,
        // Reset dependent selections
        instructor: null,
        date: null,
        time: null,
        eventId: null,
      },
    }));
  }, []);

  const selectInstructor = useCallback((instructor: IGroupClassInstructor) => {
    setWizardState((prev) => ({
      ...prev,
      selection: {
        ...prev.selection,
        instructor,
        // Reset dependent selections
        date: null,
        time: null,
        eventId: null,
      },
    }));
  }, []);

  const selectDateTime = useCallback(
    (
      date: Date,
      time: string,
      eventId: number,
      actualInstructorName?: string,
      actualInstructorId?: number,
      durationMinutes?: number,
      isWaitlistBooking?: boolean,
      requirePrepayment?: boolean,
      prepaymentLink?: string | null,
      requireFormCompletion?: boolean,
      formCompletionLink?: string | null,
      allowOnlineReservations?: boolean,
      prepaymentProvider?: number,
      price?: number,
      isMultisportAvailable?: boolean,
    ) => {
      setWizardState((prev) => ({
        ...prev,
        isWaitlistBooking: isWaitlistBooking ?? false,
        selection: {
          ...prev.selection,
          date,
          time,
          eventId,
          actualInstructorName,
          actualInstructorId,
          durationMinutes,
          requirePrepayment,
          prepaymentLink,
          prepaymentProvider,
          requireFormCompletion,
          formCompletionLink,
          allowOnlineReservations,
          price,
          isMultisportAvailable,
        },
      }));
    },
    [],
  );

  const selectDateOnly = useCallback((date: Date) => {
    setWizardState((prev) => ({
      ...prev,
      selection: {
        ...prev.selection,
        date,
        time: null,
        eventId: null,
      },
    }));
  }, []);

  const selectFamilyMember = useCallback((familyMember: IGroupClassFamilyMember) => {
    setWizardState((prev) => ({
      ...prev,
      selection: {
        ...prev.selection,
        familyMember,
      },
    }));
  }, []);

  /**
   * What "confirm" should do on the last step for the current selection: fill the
   * in-app form first, go through the payment-method dialog, or reserve straight away.
   */
  const resolveConfirmAction = useCallback(
    (needsFormSubmission: boolean, hasFormSubmission: boolean) =>
      resolveReservationConfirmAction({
        needsFormSubmission,
        hasFormSubmission,
        requirePrepayment: wizardState.selection.requirePrepayment ?? false,
        isMultisportAvailable: wizardState.selection.isMultisportAvailable ?? false,
        isWaitlistBooking: wizardState.isWaitlistBooking,
      }),
    [
      wizardState.selection.requirePrepayment,
      wizardState.selection.isMultisportAvailable,
      wizardState.isWaitlistBooking,
    ],
  );

  // Create reservation
  const createReservation = useCallback(async (
    subscriptionPlanId: number | null = null,
    formSubmission: IPreReservationFormSubmission | null = null,
    payWithMultisport: boolean = false,
  ) => {
    if (wizardState.currentStep !== "confirmation") {
      return;
    }

    const { selection } = wizardState;
    const { currentUser } = authHook;

    if (!selectedStudio || !selection.eventId || !currentUser?.id) {
      setWizardState((prev) => ({
        ...prev,
        error: "Missing required reservation information",
      }));
      return;
    }

    // Use family member's ID if selected, otherwise use current user's ID
    const userIdForReservation = selection.familyMember?.id ?? currentUser.id;

    const reservationData: ICreateGroupClassReservationRequest = {
      eventId: selection.eventId,
      userId: userIdForReservation,
      notes: "",
      subscriptionPlanId,
      payWithMultisport,
      formSubmission,
    };

    setWizardState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await reservationMutation.mutateAsync({
        endpoint: endpointsMetadata.groupClasses.reserve,
        method: "post",
        payload: reservationData,
        disableSuccessToast: true,
      });

      if (response && response.success) {
        if (response.stripeCheckoutUrl) {
          window.location.href = response.stripeCheckoutUrl;
        } else if (
          isMissingStripeCheckoutUrl({
            requirePrepayment: selection.requirePrepayment,
            prepaymentProvider: selection.prepaymentProvider,
            subscriptionPlanId,
            payWithMultisport,
            isWaitlist: response.isWaitlist,
            stripeCheckoutUrl: response.stripeCheckoutUrl,
          })
        ) {
          // Prepaid Stripe class paid by card expected a checkout URL but got none →
          // integration error. Plan, Multisport, waitlist and non-prepaid classes never get one.
          getErrorToast("Възникна грешка със Stripe интеграцията. Свържете се с администратор на обекта.");
          setWizardState((prev) => ({ ...prev, isLoading: false }));
        } else {
          setWizardState((prev) => ({
            ...prev,
            reservationId: response.reservationId,
            isWaitlistBooking: response.isWaitlist,
            isLoading: false,
          }));
          goToStep("success");
        }
      } else {
        setWizardState((prev) => ({
          ...prev,
          error: response?.message || "Failed to create reservation",
          isLoading: false,
        }));
      }
    } catch (error) {
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as any;
        const backendMessage =
          axiosError.response?.data?.message ||
          axiosError.response?.data ||
          axiosError.message;
        setWizardState((prev) => ({
          ...prev,
          error: `Failed to create reservation: ${backendMessage}`,
          isLoading: false,
        }));
      } else {
        setWizardState((prev) => ({
          ...prev,
          error: "Failed to create reservation. Please try again.",
          isLoading: false,
        }));
      }
    }
  }, [
    wizardState.currentStep,
    wizardState.selection,
    selectedStudio,
    authHook,
    reservationMutation,
    goToStep,
  ]);

  // Auth redirect trigger
  const triggerAuthRedirect = useCallback(() => {
    setPendingAuthRedirect(true);
  }, []);

  // ========== STATE RESTORATION AFTER AUTH ==========
  // Restore wizard state from localStorage after user authenticates
  useEffect(() => {
    if (
      authHook.isAuthenticated &&
      authHook.currentUser &&
      !restorationProcessedRef.current
    ) {
      const backupState = localStorage.getItem("groupClassWizardStateBackup");

      if (backupState) {
        try {
          const parsedBackup: any = JSON.parse(backupState);

          // Check expiry (10 minutes)
          const now = Date.now();
          const backupAge = now - (parsedBackup.timestamp || 0);
          const isExpired = backupAge > 10 * 60 * 1000;

          if (isExpired) {
            localStorage.removeItem("groupClassWizardStateBackup");
            return;
          }

          // Prevent double processing
          if (processedBackupRef.current === backupState) {
            return;
          }

          processedBackupRef.current = backupState;
          restorationProcessedRef.current = true;

          // Restore state and jump to family member step (auto-skipped if user has no family members)
          setWizardState((prev) => ({
            ...prev,
            currentStep: "familyMember",
            stepIndex: STEP_ORDER.indexOf("familyMember"),
            canNavigateBack: true,
            canNavigateForward: true,
            selection: {
              ...prev.selection,
              date: parsedBackup.selection?.date
                ? new Date(parsedBackup.selection.date)
                : null,
              time: parsedBackup.selection?.time || null,
              eventId: parsedBackup.selection?.eventId || null,
            },
          }));

          // Store pending IDs for validation
          if (parsedBackup.selection?.class?.className) {
            setPendingClassId(parsedBackup.selection.class.className);
          }
          if (parsedBackup.selection?.instructor?.id) {
            setPendingInstructorId(parsedBackup.selection.instructor.id);
          }

          // Update URL
          updateUrlWithStep("familyMember", false);

          localStorage.removeItem("groupClassWizardStateBackup");
        } catch (e) {
          console.error("Failed to restore group class wizard state:", e);
          localStorage.removeItem("groupClassWizardStateBackup");
        }
      }
    }
  }, [authHook.isAuthenticated, authHook.currentUser, updateUrlWithStep]);

  // ========== VALIDATE PENDING CLASS ID ==========
  // After restoration, validate that the selected class still exists
  useEffect(() => {
    if (
      pendingClassId &&
      availableClasses.length > 0 &&
      !wizardState.selection.class
    ) {
      const matchedClass = availableClasses.find(
        (c) => c.className === pendingClassId,
      );

      if (matchedClass) {
        // Class found - restore it
        setWizardState((prev) => ({
          ...prev,
          selection: {
            ...prev.selection,
            class: matchedClass,
          },
        }));
      } else {
        // Class not found - reset wizard to start
        setWizardState({
          currentStep: "classes",
          stepIndex: STEP_ORDER.indexOf("classes"),
          totalSteps: STEP_ORDER.length,
          isTransitioning: false,
          canNavigateBack: false,
          canNavigateForward: true,
          selection: {
            class: null,
            instructor: null,
            date: null,
            time: null,
            eventId: null,
          },
          isLoading: false,
          error: null,
          reservationId: null,
          isWaitlistBooking: false,
        });
        updateUrlWithStep("classes", false);
      }

      setPendingClassId(undefined);
    }
  }, [
    availableClasses,
    wizardState.selection.class,
    pendingClassId,
    updateUrlWithStep,
  ]);

  // ========== VALIDATE PENDING INSTRUCTOR ID ==========
  // After restoration, validate that the selected instructor still exists
  useEffect(() => {
    if (
      pendingInstructorId &&
      availableInstructors.length > 0 &&
      !wizardState.selection.instructor
    ) {
      const matchedInstructor = availableInstructors.find(
        (inst) => inst.id === pendingInstructorId,
      );

      if (matchedInstructor) {
        // Instructor found - restore it
        setWizardState((prev) => ({
          ...prev,
          selection: {
            ...prev.selection,
            instructor: matchedInstructor,
          },
        }));
      } else {
        // Instructor not found - reset to class selection
        setWizardState({
          currentStep: "classes",
          stepIndex: STEP_ORDER.indexOf("classes"),
          totalSteps: STEP_ORDER.length,
          isTransitioning: false,
          canNavigateBack: false,
          canNavigateForward: true,
          selection: {
            class: null,
            instructor: null,
            date: null,
            time: null,
            eventId: null,
          },
          isLoading: false,
          error: null,
          reservationId: null,
          isWaitlistBooking: false,
        });
        updateUrlWithStep("classes", false);
      }

      setPendingInstructorId(undefined);
    }
  }, [
    availableInstructors,
    wizardState.selection.instructor,
    pendingInstructorId,
    updateUrlWithStep,
  ]);

  // ========== AUTO-PROCEED FROM LOGIN AFTER AUTH ==========
  // Watch for authentication changes and auto-proceed from login to confirmation
  useEffect(() => {
    if (
      pendingAuthRedirect &&
      authHook.isAuthenticated &&
      authHook.currentUser &&
      wizardState.currentStep === "login"
    ) {
      setPendingAuthRedirect(false);
      setTimeout(() => {
        goToStep("familyMember");
      }, 50);
    }
  }, [
    authHook.isAuthenticated,
    authHook.currentUser,
    wizardState.currentStep,
    pendingAuthRedirect,
    goToStep,
  ]);

  // ========== AUTO-PROCEED FROM FAMILY MEMBER STEP IF ONLY ONE MEMBER ==========
  // Auto-proceed from family member step when data loads and there's only one member
  useEffect(() => {
    if (
      wizardState.currentStep === "familyMember" &&
      !familyMembersLoading &&
      !hasFamilyMembers &&
      availableFamilyMembers.length > 0
    ) {
      // Auto-select the only member (self) and proceed
      if (availableFamilyMembers.length === 1) {
        setWizardState((prev) => ({
          ...prev,
          selection: {
            ...prev.selection,
            familyMember: availableFamilyMembers[0],
          },
        }));
      }
      // Auto-proceed to next step
      setTimeout(() => {
        nextStep();
      }, 100);
    }
  }, [
    wizardState.currentStep,
    familyMembersLoading,
    hasFamilyMembers,
    availableFamilyMembers,
    nextStep,
  ]);

  // ========== AUTO-PROCEED AFTER INSTRUCTORS LOAD (fast-click fix) ==========
  // User clicked "next" before instructors finished loading — resolve now
  useEffect(() => {
    if (
      pendingInstructorAutoSkip &&
      !instructorsLoading &&
      wizardState.currentStep === "classes"
    ) {
      setPendingInstructorAutoSkip(false);

      const realInstructors = availableInstructors.filter(
        (inst) => inst.id !== -1,
      );
      const { isAuthenticated, currentUser } = authHook;
      let nextIndex = wizardState.stepIndex + 1;

      if (realInstructors.length === 1) {
        const singleInstructor = realInstructors[0];
        setWizardState((prev) => ({
          ...prev,
          selection: {
            ...prev.selection,
            instructor: singleInstructor,
            date: null,
            time: null,
            eventId: null,
          },
        }));
        nextIndex = wizardState.stepIndex + 2;
      }

      if (nextIndex < STEP_ORDER.length && STEP_ORDER[nextIndex] === "login") {
        if (isAuthenticated && currentUser) {
          nextIndex = nextIndex + 1;
        }
      }

      if (nextIndex < STEP_ORDER.length) {
        goToStep(STEP_ORDER[nextIndex]);
      }
    }
  }, [
    pendingInstructorAutoSkip,
    instructorsLoading,
    wizardState.currentStep,
    wizardState.stepIndex,
    availableInstructors,
    authHook,
    goToStep,
  ]);

  // Progress calculation
  const progressPercentage = useMemo(() => {
    const { isAuthenticated } = authHook;
    const realInstructors = availableInstructors.filter(
      (inst) => inst.id !== -1,
    );

    let adjustedTotalSteps = STEP_ORDER.length;
    if (isAuthenticated) adjustedTotalSteps -= 1; // Skip login
    if (realInstructors.length === 1) adjustedTotalSteps -= 1; // Skip instructor selection
    if (!hasFamilyMembers) adjustedTotalSteps -= 1; // Skip family member selection

    let adjustedCurrentIndex = wizardState.stepIndex;
    if (
      isAuthenticated &&
      wizardState.stepIndex > STEP_ORDER.indexOf("login")
    ) {
      adjustedCurrentIndex -= 1;
    }
    if (
      realInstructors.length === 1 &&
      wizardState.stepIndex > STEP_ORDER.indexOf("instructors")
    ) {
      adjustedCurrentIndex -= 1;
    }
    if (
      !hasFamilyMembers &&
      wizardState.stepIndex > STEP_ORDER.indexOf("familyMember")
    ) {
      adjustedCurrentIndex -= 1;
    }

    const calculatedProgress = Math.round((adjustedCurrentIndex / (adjustedTotalSteps - 1)) * 100);

    // Check if user went backwards (clicked back button)
    const wentBackwards = wizardState.stepIndex < prevStepIndexRef.current;

    let finalProgress: number;
    if (wentBackwards) {
      // If going backwards, allow progress to decrease
      finalProgress = calculatedProgress;
      maxProgressRef.current = calculatedProgress;
    } else {
      // If going forward or staying same, never let progress decrease
      finalProgress = Math.max(calculatedProgress, maxProgressRef.current);
      maxProgressRef.current = finalProgress;
    }

    // Update previous step index
    prevStepIndexRef.current = wizardState.stepIndex;

    return finalProgress;
  }, [wizardState.stepIndex, authHook, availableInstructors, hasFamilyMembers]);

  // Can proceed from current step
  const canProceedFromStep = useMemo(() => {
    const { currentStep, selection } = wizardState;

    switch (currentStep) {
      case "classes":
        return !!selection.class;
      case "instructors":
        return !!selection.instructor;
      case "datetime":
        return !!selection.date && !!selection.time && !!selection.eventId && selection.allowOnlineReservations !== false;
      case "login":
        return authHook.isAuthenticated;
      case "familyMember":
        return !!selection.familyMember;
      case "confirmation":
        return true;
      case "success":
        return false;
      default:
        return false;
    }
  }, [wizardState, authHook.isAuthenticated]);

  return {
    // State
    ...wizardState,
    canProceedFromStep,
    progressPercentage,

    // Data
    availableClasses,
    classCategories,
    classesByCategory,
    availableInstructors,
    availableFamilyMembers,
    hasFamilyMembers,
    selectedStudio,
    currentUser: authHook.isAuthenticated ? authHook.currentUser : null,
    classesLoading,
    instructorsLoading,
    familyMembersLoading,
    pendingInstructorAutoSkip,

    // Navigation
    goToStep,
    nextStep,
    previousStep,

    // Selection
    selectClass,
    selectInstructor,
    selectDateTime,
    selectDateOnly,
    selectFamilyMember,

    // Actions
    createReservation,
    triggerAuthRedirect,
    resolveConfirmAction,
  };
};
